import React, { useEffect, useState, useMemo } from 'react';
import { neonQuery } from '../services/api';
import OrderSummaryModal from './OrderSummaryModal';

// Ledger-backed customer detail. Reads the canonical vip_cash_ledger
// (post-Step 4c bank-style ledger). Four tabs:
//   Ledger      — real sources (mongo_*, bc_order), running balance
//   Adjustments — inferred entries posted during reconciliation
//   Pending     — pending earns from vip_cash_transactions (not in ledger)
//   Conflicts   — read-only view of this customer's conflict register
//   Issues      — read-only view of this customer's issue register
// Triage actions on conflicts/issues happen on the page-level tabs.

const fmt$ = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? '-' : '';
  return `${sign}$${Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};
const fmtSigned = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) < 0.005) return '$0.00';
  return (v > 0 ? '+' : '−') + '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
};
const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

// Source chips, color-coded so the table reads at a glance. Mongo-era sources first,
// then the SQL-era ones the connector writes.
const SOURCE_META = {
  mongo_earn:               { label: 'Mongo earn',     bg: '#e8f5e9', fg: '#2e7d32', border: '#a5d6a7' },
  mongo_redeem:             { label: 'Mongo redeem',   bg: '#fff3e0', fg: '#e65100', border: '#ffb74d' },
  mongo_expire:             { label: 'Mongo expire',   bg: '#f5f5f5', fg: '#616161', border: '#bdbdbd' },
  mongo_cancellation:       { label: 'Mongo cancel',   bg: '#f5f5f5', fg: '#616161', border: '#bdbdbd' },
  bc_order:                 { label: 'BC order',       bg: '#e3f2fd', fg: '#0d47a1', border: '#90caf9' },
  inferred_opening_balance: { label: 'Opening bal.',   bg: '#f3e5f5', fg: '#6a1b9a', border: '#ce93d8' },
  inferred_external_credit: { label: 'External credit',bg: '#f3e5f5', fg: '#6a1b9a', border: '#ce93d8' },
  inferred_external_debit:  { label: 'External debit', bg: '#f3e5f5', fg: '#6a1b9a', border: '#ce93d8' },
  bc_manual_inferred:       { label: 'Manual BC',      bg: '#f3e5f5', fg: '#6a1b9a', border: '#ce93d8' },
  // SQL-era sources (the ledger of record since 2026-08-04). Without these every
  // row written by the connector renders its Source chip as '?'.
  'status-ship':            { label: 'Earn on ship',  bg: '#e8f5e9', fg: '#2e7d32', border: '#a5d6a7' },
  'bc-redeem':              { label: 'BC redeem',     bg: '#fff3e0', fg: '#e65100', border: '#ffb74d' },
  admin:                    { label: 'Admin adj.',    bg: '#f3e5f5', fg: '#6a1b9a', border: '#ce93d8' },
  restore:                  { label: 'Restore',       bg: '#e8f5e9', fg: '#2e7d32', border: '#a5d6a7' },
  unrestore:                { label: 'Un-restore',    bg: '#f5f5f5', fg: '#616161', border: '#bdbdbd' },
  reverse:                  { label: 'Reverse',       bg: '#f5f5f5', fg: '#616161', border: '#bdbdbd' },
  unreverse:                { label: 'Un-reverse',    bg: '#e8f5e9', fg: '#2e7d32', border: '#a5d6a7' },
  merge:                    { label: 'Merge',         bg: '#e3f2fd', fg: '#0d47a1', border: '#90caf9' },
  backfill:                 { label: 'Backfill',      bg: '#f3e5f5', fg: '#6a1b9a', border: '#ce93d8' },
};
const DEFAULT_META = { label: '?', bg: '#fafafa', fg: '#757575', border: '#e0e0e0' };

function SourceBadge({ source }) {
  const m = SOURCE_META[source] || DEFAULT_META;
  return (
    <span style={{
      display: 'inline-block', fontSize: 9, fontWeight: 600, lineHeight: 1.4,
      padding: '1px 6px', borderRadius: 3, whiteSpace: 'nowrap',
      background: m.bg, color: m.fg, border: `1px solid ${m.border}`,
    }}>{m.label}</span>
  );
}

function categoryLabel(row) {
  const c = String(row.category || '').toLowerCase();
  const src = row.source;
  if (src === 'inferred_opening_balance') return 'Opening balance';
  if (src === 'inferred_external_credit') return 'External credit (manual BC)';
  if (src === 'inferred_external_debit')  return 'External debit (manual BC)';
  if (src === 'bc_manual_inferred')       return 'Manual BC adjustment';
  if (c === 'welcome')      return 'Welcome reward';
  if (c === 'monthly')      return 'Monthly reward';
  // The connector writes category 'order' for a status-ship earn; the Mongo era wrote
  // 'order_reward'. Same thing - accept both or every SQL-era earn loses its order number.
  if (c === 'order_reward' || c === 'order') return row.order_id ? `Order reward #${row.order_id}` : 'Order reward';
  if (c === 'goodwill')     return 'Goodwill credit';
  if (c === 'manual_adjustment') return row.direction === 'credit' ? 'Manual credit' : 'Manual debit';
  if (c === 'redeem')       return row.order_id ? `Redemption on #${row.order_id}` : 'Redemption';
  if (c === 'expire')       return 'Expired';
  if (c === 'cancellation') {
    // mongo_cancellation covers several distinct kinds — distinguish by
    // description so users can tell apart monthly-reward reversals,
    // phantom-order cleanups, and actual VIP membership cancellations.
    const d = String(row.description || '').toLowerCase();
    if (d.includes('monthly reward reversal')) return 'Monthly reward reversal';
    if (d.includes('phantom-order') || d.includes('phantom order')) return 'Phantom-order cancellation';
    if (d.includes('vip cancellation') || d.includes('vip membership')) return 'VIP membership cancellation';
    return row.description ? `Cancellation — ${row.description.slice(0, 50)}` : 'Cancellation';
  }
  if (c === 'audit_reconciliation') return 'Audit reconciliation';
  return row.description || c || row.direction;
}

const STATUS_COLORS = {
  open:         { bg: '#fff3e0', fg: '#e65100' },
  keep_a_only:  { bg: '#e8f5e9', fg: '#2e7d32' },
  keep_b_only:  { bg: '#e8f5e9', fg: '#2e7d32' },
  keep_both:    { bg: '#e3f2fd', fg: '#0d47a1' },
  keep_neither: { bg: '#fafafa', fg: '#616161' },
  acknowledged: { bg: '#fafafa', fg: '#616161' },
  resolved:     { bg: '#e8f5e9', fg: '#2e7d32' },
  dismissed:    { bg: '#fafafa', fg: '#9e9e9e' },
};

function StatusPill({ status }) {
  const s = STATUS_COLORS[status] || { bg: '#fafafa', fg: '#616161' };
  return (
    <span style={{
      display: 'inline-block', fontSize: 9, fontWeight: 700, lineHeight: 1.4,
      padding: '1px 6px', borderRadius: 9, whiteSpace: 'nowrap',
      background: s.bg, color: s.fg, textTransform: 'uppercase', letterSpacing: 0.3,
    }}>{String(status || '').replace(/_/g, ' ')}</span>
  );
}

function Metric({ label, sub, value, color }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
        {label}
        {sub && <span style={{ marginLeft: 4, fontSize: 9, textTransform: 'none', letterSpacing: 0, color: 'var(--gray-400)' }}>({sub})</span>}
      </div>
      <div style={{ fontSize: 16, fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function Divider() {
  return <div style={{ width: 1, height: 32, background: 'var(--gray-200)' }} />;
}

export default function VipRewardsModal({ open, onClose, bcCustomerId, customerName, storeCredit, showDrift = false }) {
  const [tab, setTab] = useState('ledger');
  const [showExcluded, setShowExcluded] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [pending, setPending] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [drillOrderId, setDrillOrderId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);

  useEffect(() => {
    if (!open || !bcCustomerId) return;
    let cancelled = false;
    setLedger([]); setPending([]); setConflicts([]); setIssues([]);
    setError(null); setLoading(true); setExpandedId(null); setTab('ledger');
    Promise.all([
      neonQuery('vip_cash_ledger_by_customer_id', { bcCustomerId: String(bcCustomerId) }),
      neonQuery('vip_cash_ledger_pending_by_customer', { bcCustomerId: String(bcCustomerId) }),
      neonQuery('vip_cash_ledger_conflicts_by_customer', { bcCustomerId: String(bcCustomerId) }),
      neonQuery('vip_cash_ledger_issues_by_customer', { bcCustomerId: String(bcCustomerId) }),
    ])
      .then(([l, p, c, i]) => {
        if (cancelled) return;
        setLedger(Array.isArray(l) ? l : []);
        setPending(Array.isArray(p) ? p : []);
        setConflicts(Array.isArray(c) ? c : []);
        setIssues(Array.isArray(i) ? i : []);
      })
      .catch((e) => { if (!cancelled) setError(e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, bcCustomerId]);

  const refreshThisCustomer = async () => {
    if (!bcCustomerId) return;
    setRefreshing(true); setRefreshMsg(null);
    try {
      const base = window.location.hostname === 'localhost'
        ? 'http://localhost:8888/.netlify/functions'
        : '/.netlify/functions';
      // Re-pull this customer from upstream, then append any new ledger rows.
      const res = await fetch(`${base}/sync-vip-cash-rewards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mode: 'customer', customerId: String(bcCustomerId), force: true, triggeredBy: 'modal-refresh' }),
      });
      const text = await res.text();
      let data;
      try { data = JSON.parse(text); }
      catch { throw new Error('Sync function returned non-JSON'); }
      if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);
      await neonQuery('vip_cash_ledger_append');
      const [l, p, c, i] = await Promise.all([
        neonQuery('vip_cash_ledger_by_customer_id', { bcCustomerId: String(bcCustomerId) }),
        neonQuery('vip_cash_ledger_pending_by_customer', { bcCustomerId: String(bcCustomerId) }),
        neonQuery('vip_cash_ledger_conflicts_by_customer', { bcCustomerId: String(bcCustomerId) }),
        neonQuery('vip_cash_ledger_issues_by_customer', { bcCustomerId: String(bcCustomerId) }),
      ]);
      setLedger(Array.isArray(l) ? l : []);
      setPending(Array.isArray(p) ? p : []);
      setConflicts(Array.isArray(c) ? c : []);
      setIssues(Array.isArray(i) ? i : []);
      setRefreshMsg(`Refreshed: +${data.inserted || 0} new mongo txns`);
    } catch (e) {
      setRefreshMsg(`Refresh failed: ${e.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  // Split for the Adjustments tab (filtered view) and the Ledger tab (full view).
  const isInferred = (r) => r && r.source && (r.source.startsWith('inferred_') || r.source === 'bc_manual_inferred');
  const adjustmentRows = useMemo(() => ledger.filter(isInferred), [ledger]);

  // Running balance over ALL included ledger rows (real + inferred), ordered
  // by txn_date ASC. Final balance reflects BC store_credit. Excluded rows
  // appear muted when the toggle is on; they don't move the balance.
  const ledgerWithBalance = useMemo(() => {
    const visible = showExcluded ? ledger : ledger.filter(r => r.included_in_balance);
    let bal = 0;
    return visible.map(r => {
      const signed = (r.direction === 'credit' ? 1 : -1) * Number(r.amount || 0);
      if (r.included_in_balance) bal += signed;
      return { ...r, _signed: signed, _balance: bal };
    });
  }, [ledger, showExcluded]);

  // Raw ledger sum: SUM(credit) − SUM(debit) across ALL included rows
  // (including inferred/goodwill). Can go negative when a customer redeems
  // credit that wasn't earned via trackable channels.
  const rawSum = useMemo(() => {
    return ledger.reduce((s, r) => {
      if (!r.included_in_balance) return s;
      return s + (r.direction === 'credit' ? 1 : -1) * Number(r.amount || 0);
    }, 0);
  }, [ledger]);

  // FIFO real balance: walk included rows chronologically, queue real earns,
  // consume them FIFO on bc_order debits and mongo_cancellation links.
  // Skips inferred/goodwill/audit_reconciliation/mongo_expire/mongo_redeem —
  // these are excluded from the "true earned" reconciliation.
  const fifoReal = useMemo(() => {
    const isInferredSrc = (s) => s === 'inferred' || s === 'bc_manual_inferred' || (s || '').startsWith('inferred_');
    const isGoodwillDesc = (d) => { const x = String(d || '').toLowerCase(); return x.includes('goodwill restoration') || x.includes('retention sweep'); };
    const isAuditReconDesc = (d) => String(d || '').toLowerCase().includes('audit reconciliation');
    const evs = [...ledger].sort((a, b) => {
      const da = new Date(a.txn_date).getTime(), db = new Date(b.txn_date).getTime();
      return da !== db ? da - db : Number(a.id) - Number(b.id);
    });
    const queue = [];
    for (const r of evs) {
      if (!r.included_in_balance) continue;
      if (isInferredSrc(r.source) || isGoodwillDesc(r.description) || isAuditReconDesc(r.description)) continue;
      const amt = Number(r.amount || 0);
      if (r.source === 'mongo_earn') {
        queue.push({ mongo_id: (r.evidence && r.evidence._id) || '', remaining: amt });
      } else if (r.source === 'bc_order') {
        let need = amt;
        for (const e of queue) { if (need <= 0) break; if (e.remaining <= 0) continue; const t = Math.min(e.remaining, need); e.remaining -= t; need -= t; }
      } else if (r.source === 'mongo_expire' || r.source === 'mongo_redeem') {
        // Only INCLUDED rows reach here — bogus ones already excluded.
        // A surviving row is a real BC-honored debit and must consume FIFO.
        let need = amt;
        for (const e of queue) { if (need <= 0) break; if (e.remaining <= 0) continue; const t = Math.min(e.remaining, need); e.remaining -= t; need -= t; }
      } else if (r.source === 'status-ship') {
        // The SQL-era earn. Same role as mongo_earn: it funds the queue. Before this
        // branch existed every post-2026-08-04 earn was skipped, so FIFO reported a
        // customer's stale Mongo-era balance and the Drift KPI was pure fiction.
        queue.push({ mongo_id: '', remaining: amt });
      } else if (r.source === 'bc-redeem') {
        // The SQL-era redemption, mirrored from BigCommerce. Consumes like bc_order.
        let need = amt;
        for (const e of queue) { if (need <= 0) break; if (e.remaining <= 0) continue; const t = Math.min(e.remaining, need); e.remaining -= t; need -= t; }
      } else if (r.source === 'mongo_cancellation') {
        const links = Array.isArray(r.evidence && r.evidence.linked_earned_transactions) ? r.evidence.linked_earned_transactions : [];
        let consumed = 0;
        for (const link of links) {
          const lid = link && link.id; if (!lid) continue;
          const e = queue.find(x => x.mongo_id === lid);
          if (e && e.remaining > 0) { const t = Math.min(e.remaining, amt - consumed); e.remaining -= t; consumed += t; if (consumed >= amt) break; }
        }
        { let need = amt - consumed; for (const e of queue) { if (need <= 0) break; if (e.remaining <= 0) continue; const t = Math.min(e.remaining, need); e.remaining -= t; need -= t; } } // orphan-cancellation fallback
      } else {
        // Remaining SQL-era sources - admin, restore, unrestore, reverse, unreverse,
        // merge, backfill. They carry no mongo linkage, so direction is the only
        // signal: a credit funds the queue, a debit consumes it. Falling through
        // without this left them out of FIFO entirely.
        if (r.direction === 'credit') {
          queue.push({ mongo_id: '', remaining: amt });
        } else {
          let need = amt;
          for (const e of queue) { if (need <= 0) break; if (e.remaining <= 0) continue; const t = Math.min(e.remaining, need); e.remaining -= t; need -= t; }
        }
      }
    }
    return queue.reduce((s, e) => s + e.remaining, 0);
  }, [ledger]);

  const bcMirror = Number(storeCredit ?? 0);
  const drift = bcMirror - fifoReal;
  const imbalance = fifoReal - rawSum;

  // Pull email from any ledger row's evidence if we have it (mongo rows carry
  // customer_email in their raw mongo payload).
  const customerEmail = useMemo(() => {
    for (const r of ledger) {
      const e = r.evidence;
      if (e && typeof e === 'object' && (e.customer_email || (e.raw && e.raw.customer_email))) {
        return e.customer_email || e.raw.customer_email;
      }
    }
    return null;
  }, [ledger]);

  const excludedCount = ledger.length - ledger.filter(r => r.included_in_balance).length;
  const openConflicts = conflicts.filter(c => c.status === 'open').length;
  const openIssues = issues.filter(i => i.status === 'open').length;

  if (!open) return null;

  const TABS = [
    { key: 'ledger',      label: 'Ledger',      count: ledger.length },
    { key: 'adjustments', label: 'Adjustments', count: adjustmentRows.length },
    { key: 'pending',     label: 'Pending',     count: pending.length },
    { key: 'conflicts',   label: 'Conflicts',   count: conflicts.length, openCount: openConflicts },
    { key: 'issues',      label: 'Issues',      count: issues.length, openCount: openIssues },
  ];

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(10, 20, 40, 0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
    >
      <div
        className="card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(980px, 100%)', maxHeight: '88vh', display: 'flex', flexDirection: 'column', margin: 0 }}
      >
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span>
            VIP Cash Rewards{customerName ? ` — ${customerName}` : ''}
            {customerEmail && (
              <span style={{ color: 'var(--gray-500)', fontWeight: 400, marginLeft: 8 }}>· {customerEmail}</span>
            )}
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
            {refreshMsg && (
              <span style={{ fontSize: 10, color: 'var(--gray-500)' }}>{refreshMsg}</span>
            )}
            <button
              onClick={refreshThisCustomer}
              disabled={refreshing || !bcCustomerId}
              className="btn"
              style={{ fontSize: 10, padding: '3px 10px', opacity: refreshing ? 0.5 : 1 }}
              title="Force-resync this customer from upstream and append to ledger"
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            <a onClick={onClose} style={{ cursor: 'pointer', color: 'var(--gray-500)', fontSize: 18, fontWeight: 700, lineHeight: 1 }}>×</a>
          </span>
        </div>

        {/* Summary strip — snapshot from the nightly sync */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--gray-100)', fontSize: 12 }}>
          <div style={{ fontSize: 9, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600, marginBottom: 8 }}>
            Snapshot — last nightly sync
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center' }}>
            <Metric label="BC Store Credit" sub="mirror" value={storeCredit === null || storeCredit === undefined ? '…' : fmt$(storeCredit)} color={Number(storeCredit) > 0 ? 'var(--green, #2e7d32)' : 'var(--gray-700)'} />
            {showDrift && (
              <>
                <Divider />
                <Metric label="Ledger (Clean)" value={fmt$(fifoReal)} color="var(--navy)" />
                <Divider />
                <Metric
                  label="Drift"
                  sub="BC − FIFO"
                  value={Math.abs(drift) < 0.01 ? '$0.00 ✓' : fmtSigned(drift)}
                  color={Math.abs(drift) < 0.01 ? 'var(--green, #2e7d32)' : (Math.abs(drift) > 50 ? 'var(--red, #c0392b)' : 'var(--orange)')}
                />
                <Divider />
                <Metric label="Ledger (Raw)" sub="ΣCr − ΣDr" value={fmt$(rawSum)} color={rawSum < 0 ? 'var(--red, #c0392b)' : 'var(--gray-700)'} />
                <Divider />
                <Metric
                  label="Imbalance"
                  sub="FIFO − Raw"
                  value={Math.abs(imbalance) < 0.01 ? '$0.00 ✓' : fmt$(imbalance)}
                  color={Math.abs(imbalance) < 0.01 ? 'var(--green, #2e7d32)' : 'var(--orange)'}
                />
              </>
            )}
            {bcCustomerId && (
              <div style={{ marginLeft: 'auto', color: 'var(--gray-500)', fontSize: 11 }}>BC #{bcCustomerId}</div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--gray-200)', background: '#fff' }}>
          {TABS.map(t => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => { setTab(t.key); setExpandedId(null); }}
                style={{
                  background: 'transparent', border: 'none',
                  borderBottom: active ? '2px solid var(--orange)' : '2px solid transparent',
                  padding: '10px 14px', fontSize: 12,
                  fontWeight: active ? 700 : 600,
                  color: active ? 'var(--navy)' : 'var(--gray-500)',
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                {t.label}
                <span style={{
                  fontSize: 9, fontWeight: 700, color: '#fff',
                  background: t.openCount > 0 ? 'var(--red, #c0392b)' : 'var(--gray-400, #9e9e9e)',
                  borderRadius: 9, padding: '1px 6px', minWidth: 14, textAlign: 'center',
                }}>{t.count}</span>
              </button>
            );
          })}
        </div>

        <div style={{ overflow: 'auto', flex: 1 }}>
          {error && <div style={{ padding: 16, fontSize: 12, color: 'var(--red, #c0392b)' }}>Failed to load: {error}</div>}
          {loading && <div style={{ padding: 12, fontSize: 12, color: 'var(--gray-500)', textAlign: 'center' }}>Loading…</div>}

          {!loading && !error && tab === 'ledger' && (
            <LedgerTab
              rows={ledgerWithBalance}
              showExcluded={showExcluded}
              setShowExcluded={setShowExcluded}
              excludedCount={excludedCount}
              expandedId={expandedId}
              setExpandedId={setExpandedId}
              setDrillOrderId={setDrillOrderId}
            />
          )}
          {!loading && !error && tab === 'adjustments' && (
            <AdjustmentsTab rows={adjustmentRows} expandedId={expandedId} setExpandedId={setExpandedId} />
          )}
          {!loading && !error && tab === 'pending' && (
            <PendingTab rows={pending} setDrillOrderId={setDrillOrderId} />
          )}
          {!loading && !error && tab === 'conflicts' && (
            <ConflictsTab rows={conflicts} />
          )}
          {!loading && !error && tab === 'issues' && (
            <IssuesTab rows={issues} />
          )}
        </div>
      </div>

      <OrderSummaryModal
        open={!!drillOrderId}
        onClose={() => setDrillOrderId(null)}
        bcOrderId={drillOrderId}
      />
    </div>
  );
}

// ── Tab: Ledger ─────────────────────────────────────────────────────────────

function LedgerTab({ rows, enrichment, showExcluded, setShowExcluded, excludedCount, expandedId, setExpandedId, setDrillOrderId }) {
  // Render newest-first for scrolling, but the balance column reflects the
  // ascending running tally (already computed by parent).
  const display = useMemo(() => [...rows].reverse(), [rows]);
  const { expiredBy, earnsByOrder, goodwillRow } = enrichment || {};

  return (
    <>
      <div style={{ padding: '8px 16px', background: 'var(--gray-50)', fontSize: 11, color: 'var(--gray-700)', borderBottom: '1px solid var(--gray-100)', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span>{rows.length} entries</span>
        {excludedCount > 0 && (
          <label style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={showExcluded}
              onChange={(e) => setShowExcluded(e.target.checked)}
            />
            <span>Show {excludedCount} excluded {excludedCount === 1 ? 'row' : 'rows'} <span style={{ color: 'var(--gray-500)' }}>(resolved conflict half)</span></span>
          </label>
        )}
      </div>
      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)', position: 'sticky', top: 0 }}>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
            <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Description</th>
            <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Source</th>
            <th style={{ textAlign: 'right', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Amount</th>
            <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Balance</th>
          </tr>
        </thead>
        <tbody>
          {display.length === 0 && (
            <tr><td colSpan={5} style={{ padding: 16, color: 'var(--gray-500)', textAlign: 'center' }}>No ledger entries.</td></tr>
          )}
          {display.map((row, i) => {
            const expanded = expandedId === row.id;
            const excluded = !row.included_in_balance;
            const amountColor = excluded ? 'var(--gray-400, #9e9e9e)'
              : row.direction === 'credit' ? 'var(--green, #2e7d32)'
              : row.category === 'redeem' ? 'var(--orange)'
              : 'var(--gray-500)';
            return (
              <React.Fragment key={row.id}>
                <tr
                  onClick={() => setExpandedId(expanded ? null : row.id)}
                  style={{
                    borderBottom: '1px solid var(--gray-100)',
                    background: i % 2 === 0 ? '#fff' : 'var(--gray-50)',
                    opacity: excluded ? 0.5 : 1,
                    cursor: 'pointer',
                    textDecoration: excluded ? 'line-through' : 'none',
                  }}
                >
                  <td style={{ padding: '7px 12px', color: 'var(--gray-500)', whiteSpace: 'nowrap', verticalAlign: 'top' }}>
                    {fmtDate(row.txn_date)}
                    {row.order_date && fmtDate(row.order_date) !== fmtDate(row.txn_date) && (
                      <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>ordered {fmtDate(row.order_date)}</div>
                    )}
                  </td>
                  <td
                    style={{ padding: '7px 8px', color: 'var(--navy)', verticalAlign: 'top' }}
                    onClick={row.order_id ? (e) => { e.stopPropagation(); setDrillOrderId(row.order_id); } : undefined}
                  >
                    <span style={row.order_id ? { borderBottom: '1px dashed rgba(0,59,92,0.35)', cursor: 'pointer' } : undefined}>
                      {categoryLabel(row)}
                    </span>
                    {row.order_amount && (row.category === 'order_reward' || row.category === 'order') && Number(row.split_count || 0) <= 1 && (
                      <div style={{ fontSize: 10, color: 'var(--gray-500)', marginTop: 2 }}>
                        {fmt$(row.order_amount)} × {((Number(row.amount) / Number(row.order_amount)) * 100).toFixed(1)}% = {fmt$(row.amount)}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '7px 8px', verticalAlign: 'top' }}>
                    <SourceBadge source={row.source} />
                  </td>
                  <td style={{ padding: '7px 8px', textAlign: 'right', fontWeight: 600, color: amountColor, verticalAlign: 'top' }}>
                    {fmtSigned(row._signed)}
                  </td>
                  <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600, color: excluded ? 'var(--gray-400, #9e9e9e)' : 'var(--navy)', verticalAlign: 'top' }}>
                    {fmt$(row._balance)}
                  </td>
                </tr>
                {expanded && (
                  <tr style={{ background: '#fafbfd' }}>
                    <td colSpan={5} style={{ padding: '8px 16px', borderBottom: '1px solid var(--gray-200)' }}>
                      <EvidenceBlock row={row} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

function EvidenceBlock({ row }) {
  return (
    <div style={{ fontSize: 11, color: 'var(--gray-700)', lineHeight: 1.5 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 6 }}>
        <span><strong>Source ID:</strong> {row.source_id || '—'}</span>
        <span><strong>Category:</strong> {row.category || '—'}</span>
        <span><strong>Direction:</strong> {row.direction}</span>
        {row.order_id && <span><strong>Order:</strong> #{row.order_id}</span>}
        <span><strong>Posted:</strong> {fmtDateTime(row.synced_at)}</span>
      </div>
      {row.description && (
        <div style={{ marginBottom: 6 }}><strong>Description:</strong> {row.description}</div>
      )}
      {row.evidence && (
        <details>
          <summary style={{ cursor: 'pointer', color: 'var(--gray-500)' }}>Evidence (raw)</summary>
          <pre style={{ marginTop: 6, padding: 8, background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 3, fontSize: 10, overflowX: 'auto', maxHeight: 240 }}>
            {JSON.stringify(row.evidence, null, 2)}
          </pre>
        </details>
      )}
    </div>
  );
}

// ── Tab: Adjustments ────────────────────────────────────────────────────────

function AdjustmentsTab({ rows, expandedId, setExpandedId }) {
  if (rows.length === 0) {
    return <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)', textAlign: 'center' }}>
      No reconciliation adjustments for this customer.
    </div>;
  }
  return (
    <>
      <div style={{ padding: '8px 16px', background: '#f3e5f5', fontSize: 11, color: '#4a148c', borderBottom: '1px solid #ce93d8' }}>
        Synthetic ledger entries posted during the historical reconciliation to balance this customer's books against BC. Each one represents an unexplained gap that was settled in bulk on 2026-05-09–10. Going forward, real activity should not generate new adjustments.
      </div>
      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Date</th>
            <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Kind</th>
            <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Source</th>
            <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const expanded = expandedId === row.id;
            const signed = (row.direction === 'credit' ? 1 : -1) * Number(row.amount || 0);
            return (
              <React.Fragment key={row.id}>
                <tr
                  onClick={() => setExpandedId(expanded ? null : row.id)}
                  style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)', cursor: 'pointer' }}
                >
                  <td style={{ padding: '7px 12px', color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>{fmtDate(row.txn_date)}</td>
                  <td style={{ padding: '7px 8px', color: 'var(--navy)' }}>{categoryLabel(row)}</td>
                  <td style={{ padding: '7px 8px' }}><SourceBadge source={row.source} /></td>
                  <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600, color: row.direction === 'credit' ? 'var(--green, #2e7d32)' : 'var(--orange)' }}>
                    {fmtSigned(signed)}
                  </td>
                </tr>
                {expanded && (
                  <tr style={{ background: '#fafbfd' }}>
                    <td colSpan={4} style={{ padding: '8px 16px', borderBottom: '1px solid var(--gray-200)' }}>
                      <EvidenceBlock row={row} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </>
  );
}

// ── Tab: Pending ────────────────────────────────────────────────────────────

function PendingTab({ rows, setDrillOrderId }) {
  if (rows.length === 0) {
    return <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)', textAlign: 'center' }}>
      No pending earns. Pending entries are earns from orders that haven't shipped yet — they post to the ledger once the order ships.
    </div>;
  }
  return (
    <>
      <div style={{ padding: '8px 16px', background: '#fff8e1', fontSize: 11, color: '#5d4037', borderBottom: '1px solid #ffe082' }}>
        Earns from orders that haven't shipped yet. These are intentionally <strong>not</strong> in the ledger — they post automatically when the order ships and the next sync runs.
      </div>
      <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ background: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
            <th style={{ textAlign: 'left', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Order date</th>
            <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Order</th>
            <th style={{ textAlign: 'left', padding: '8px 8px', fontWeight: 700, color: 'var(--gray-500)' }}>Status</th>
            <th style={{ textAlign: 'right', padding: '8px 12px', fontWeight: 700, color: 'var(--gray-500)' }}>Pending earn</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id} style={{ borderBottom: '1px solid var(--gray-100)', background: i % 2 === 0 ? '#fff' : 'var(--gray-50)' }}>
              <td style={{ padding: '7px 12px', color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>{fmtDate(row.order_date || row.created_at)}</td>
              <td
                style={{ padding: '7px 8px', color: 'var(--navy)', cursor: row.order_id ? 'pointer' : 'default' }}
                onClick={row.order_id ? () => setDrillOrderId(row.order_id) : undefined}
              >
                <span style={row.order_id ? { borderBottom: '1px dashed rgba(0,59,92,0.35)' } : undefined}>
                  {row.order_id ? `Order #${row.order_id}` : 'Pending earn'}
                </span>
                {row.order_amount > 0 && (
                  <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{fmt$(row.order_amount)}</div>
                )}
              </td>
              <td style={{ padding: '7px 8px', color: 'var(--gray-700)', fontSize: 11 }}>{row.order_status || '—'}</td>
              <td style={{ padding: '7px 12px', textAlign: 'right', fontWeight: 600, color: 'var(--gray-500)' }}>{fmt$(row.amount)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

// ── Tab: Conflicts ──────────────────────────────────────────────────────────

function ConflictsTab({ rows }) {
  if (rows.length === 0) {
    return <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)', textAlign: 'center' }}>
      No conflicts for this customer. ✓
    </div>;
  }
  return (
    <div style={{ padding: 12 }}>
      <div style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 8 }}>
        Read-only — triage actions live on the page-level Conflicts tab.
      </div>
      {rows.map(c => (
        <div key={c.id} style={{ border: '1px solid var(--gray-200)', borderRadius: 4, padding: 10, marginBottom: 8, background: c.status === 'open' ? '#fff8e1' : '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 12 }}>{c.conflict_type}</span>
            <StatusPill status={c.status} />
            <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--gray-500)' }}>{fmtDate(c.detected_at)}</span>
          </div>
          {c.summary && <div style={{ fontSize: 11, color: 'var(--gray-700)', marginBottom: 6 }}>{c.summary}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
            <div style={{ padding: 6, background: 'var(--gray-50)', borderRadius: 3 }}>
              <div style={{ fontSize: 9, color: 'var(--gray-500)', textTransform: 'uppercase' }}>A</div>
              {c.source_a && <SourceBadge source={c.source_a} />}
              <div style={{ marginTop: 4 }}>{fmt$(c.amount_a)} on {fmtDate(c.txn_date_a)}</div>
              <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>id #{c.ledger_id_a || '—'}</div>
            </div>
            <div style={{ padding: 6, background: 'var(--gray-50)', borderRadius: 3 }}>
              <div style={{ fontSize: 9, color: 'var(--gray-500)', textTransform: 'uppercase' }}>B</div>
              {c.source_b && <SourceBadge source={c.source_b} />}
              <div style={{ marginTop: 4 }}>{fmt$(c.amount_b)} on {fmtDate(c.txn_date_b)}</div>
              <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>id #{c.ledger_id_b || '—'}</div>
            </div>
          </div>
          {Math.abs(Number(c.delta || 0)) > 0 && (
            <div style={{ marginTop: 6, fontSize: 11 }}>
              <strong>Δ</strong> {fmtSigned(c.delta)}
            </div>
          )}
          {c.resolution_note && (
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--gray-700)', fontStyle: 'italic' }}>
              "{c.resolution_note}"{c.resolved_by ? <span style={{ color: 'var(--gray-500)' }}> — {c.resolved_by}</span> : null}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Tab: Issues ─────────────────────────────────────────────────────────────

function IssuesTab({ rows }) {
  if (rows.length === 0) {
    return <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-500)', textAlign: 'center' }}>
      No issues for this customer. ✓
    </div>;
  }
  return (
    <div style={{ padding: 12 }}>
      <div style={{ fontSize: 10, color: 'var(--gray-500)', marginBottom: 8 }}>
        Read-only — triage actions live on the page-level Issues tab.
      </div>
      {rows.map(i => (
        <div key={i.id} style={{ border: '1px solid var(--gray-200)', borderRadius: 4, padding: 10, marginBottom: 8, background: i.status === 'open' ? '#fff8e1' : '#fff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontWeight: 700, color: 'var(--navy)', fontSize: 12 }}>{i.issue_type}</span>
            <SeverityPill severity={i.severity} />
            <StatusPill status={i.status} />
            <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--gray-500)' }}>{fmtDate(i.detected_at)}</span>
          </div>
          {i.summary && <div style={{ fontSize: 11, color: 'var(--gray-700)', marginBottom: 6 }}>{i.summary}</div>}
          {i.ledger_amount && (
            <div style={{ fontSize: 11 }}>
              {i.ledger_source && <SourceBadge source={i.ledger_source} />}
              {' '}
              <strong>{i.ledger_direction === 'credit' ? '+' : '−'}{fmt$(i.ledger_amount)}</strong>
              {' · ledger id #'}{i.ledger_id}
            </div>
          )}
          {i.source_ref && (
            <details style={{ marginTop: 6 }}>
              <summary style={{ cursor: 'pointer', fontSize: 10, color: 'var(--gray-500)' }}>Upstream reference</summary>
              <pre style={{ marginTop: 4, padding: 6, background: '#fafafa', borderRadius: 3, fontSize: 10, overflowX: 'auto' }}>
                {JSON.stringify(i.source_ref, null, 2)}
              </pre>
            </details>
          )}
          {i.resolution_note && (
            <div style={{ marginTop: 6, fontSize: 11, color: 'var(--gray-700)', fontStyle: 'italic' }}>
              "{i.resolution_note}"{i.resolved_by ? <span style={{ color: 'var(--gray-500)' }}> — {i.resolved_by}</span> : null}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const SEVERITY_COLORS = {
  needs_action: { bg: '#ffebee', fg: '#c62828' },
  warn:         { bg: '#fff3e0', fg: '#e65100' },
  info:         { bg: '#e3f2fd', fg: '#0d47a1' },
};
function SeverityPill({ severity }) {
  const s = SEVERITY_COLORS[severity] || { bg: '#fafafa', fg: '#616161' };
  return (
    <span style={{
      display: 'inline-block', fontSize: 9, fontWeight: 700, lineHeight: 1.4,
      padding: '1px 6px', borderRadius: 9, whiteSpace: 'nowrap',
      background: s.bg, color: s.fg, textTransform: 'uppercase', letterSpacing: 0.3,
    }}>{severity}</span>
  );
}
